import {initForm} from './form.js';

initForm();

/* ===== Submenu ===== */
(() => {
    const submenu = document.querySelector('.submenu');
    if (!submenu) return;

    const OFFSET = 144;
    const items = Array.from(submenu.querySelectorAll('.submenu__list-item'));
    const hashLinks = Array.from(submenu.querySelectorAll('a[href^="#"]'));
    const sections = hashLinks
        .map(link => document.getElementById(decodeURIComponent(link.getAttribute('href').slice(1))))
        .filter(Boolean);

    new IntersectionObserver(
        ([entry]) => submenu.classList.toggle('is-stuck', entry.intersectionRatio < 1),
        {threshold: [1]}
    ).observe(submenu);

    let ticking = false;
    let isAutoScrolling = false;
    let autoScrollTimer = null;
    let currentActiveId = null;

    const stopAutoScrolling = () => {
        isAutoScrolling = false;
        clearTimeout(autoScrollTimer);
        autoScrollTimer = null;
    };

    const setActive = (id) => {
        if (!id || id === currentActiveId) return;
        currentActiveId = id;
        items.forEach(item => item.classList.remove('active'));
        hashLinks.forEach(link => link.removeAttribute('aria-current'));

        const link = hashLinks.find(item =>
            decodeURIComponent(item.getAttribute('href').slice(1)) === id
        );
        if (!link) return;

        link.setAttribute('aria-current', 'true');
        link.closest('.submenu__list-item')?.classList.add('active');
    };

    const scrollToSection = (id, updateHash = true) => {
        const target = document.getElementById(id);
        if (!target) return;

        isAutoScrolling = true;
        window.scrollTo({
            top: Math.max(0, window.scrollY + target.getBoundingClientRect().top - OFFSET),
            behavior: 'smooth',
        });
        autoScrollTimer = setTimeout(stopAutoScrolling, 800);

        if (updateHash) history.pushState(null, '', `#${encodeURIComponent(id)}`);
    };

    hashLinks.forEach(link => link.addEventListener('click', event => {
        event.preventDefault();
        const id = decodeURIComponent(link.getAttribute('href').slice(1));
        scrollToSection(id);
        setActive(id);
    }));

    const updateOnScroll = () => {
        if (ticking || isAutoScrolling) return;
        ticking = true;
        requestAnimationFrame(() => {
            ticking = false;
            const current = sections.find(section => {
                const rect = section.getBoundingClientRect();
                return rect.top <= OFFSET && rect.bottom > OFFSET;
            });
            if (current) setActive(current.id);
        });
    };

    window.addEventListener('scrollend', stopAutoScrolling);
    window.addEventListener('scroll', updateOnScroll, {passive: true});
    window.addEventListener('resize', updateOnScroll);
    window.addEventListener('hashchange', () => {
        const id = decodeURIComponent(location.hash.slice(1));
        if (id) {
            scrollToSection(id, false);
            setActive(id);
        }
    });

    const initialId = decodeURIComponent(location.hash.slice(1));
    if (initialId && document.getElementById(initialId)) {
        scrollToSection(initialId, false);
        setActive(initialId);
    } else {
        updateOnScroll();
    }
})();

/* ===== Main image lightbox ===== */
document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.hero__image').forEach(root => {
        const mainHolder = root.querySelector('.hero__image-main .holder');
        const image = mainHolder?.querySelector('img');
        const zoomButton = root.querySelector('.hero__image-main .icon-zoom');

        if (!mainHolder || !image) return;

        image.setAttribute('draggable', 'false');

        root.addEventListener('dragstart', event => event.preventDefault());

        let lockY = 0;

        const lockScroll = locked => {
            const body = document.body;

            if (locked) {
                lockY = window.scrollY;
                body.style.position = 'fixed';
                body.style.top = `-${lockY}px`;
                body.style.left = '0';
                body.style.right = '0';
                body.style.width = '100%';
            } else {
                body.style.position = '';
                body.style.top = '';
                body.style.left = '';
                body.style.right = '';
                body.style.width = '';
                window.scrollTo(0, lockY);
            }
        };

        const openLightbox = () => {
            const activeImage = mainHolder.querySelector('img');
            if (!activeImage) return;

            const lightbox = document.createElement('div');
            lightbox.className = 'lb';
            lightbox.setAttribute('role', 'dialog');
            lightbox.setAttribute('aria-modal', 'true');
            lightbox.setAttribute('aria-label', 'Podgląd zdjęcia');

            lightbox.innerHTML = `
                <button class="lb__btn lb__close" aria-label="Zamknij (Esc)"></button>
                <div class="lb__stage"><img class="lb__img" alt=""></div>
            `;

            const lightboxImage = lightbox.querySelector('.lb__img');
            const closeButton = lightbox.querySelector('.lb__close');

            // SLICD-34: na stronie jest mniejsza wersja (srcset) - powiększenie pokazuje oryginał.
            lightboxImage.src = activeImage.dataset.largeImage || activeImage.currentSrc || activeImage.src;
            lightboxImage.alt = activeImage.alt;

            document.body.appendChild(lightbox);
            lockScroll(true);

            const close = () => {
                lightbox.classList.add('is-leaving');

                const remove = () => {
                    lightbox.remove();
                    lockScroll(false);
                };

                lightbox.addEventListener('animationend', remove, {once: true});
                setTimeout(remove, 400);
                document.removeEventListener('keydown', onKey);
            };

            const onKey = event => {
                if (event.key === 'Escape') close();
            };

            closeButton.addEventListener('click', close);

            lightbox.addEventListener('click', event => {
                if (event.target === lightbox) close();
            });

            document.addEventListener('keydown', onKey);
            closeButton.focus();
        };

        zoomButton?.addEventListener('click', event => {
            event.preventDefault();
            openLightbox();
        });

        mainHolder.addEventListener('dblclick', openLightbox);
    });
});

/* ===== Hero image slider ===== */
document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.hero__image').forEach(root => {
        const holder = root.querySelector('.hero__image-main .holder');

        if (!root || !holder) return;

        const thumbsWrap = root.querySelector('.hero__image-thumbs');
        const thumbItems = Array.from(root.querySelectorAll('.thumbs__item'));
        const prevButton = root.querySelector('.hero__image-main .icon-left');
        const nextButton = root.querySelector('.hero__image-main .icon-right');

        const getImageData = item => {
            const image = item.querySelector('img');
            return {src: image?.getAttribute('src') || '', alt: image?.alt || ''};
        };

        const fallback = holder.querySelector('img');
        const sources = thumbItems.length
            ? thumbItems.map(getImageData)
            : fallback
                ? [{src: fallback.getAttribute('src') || fallback.src, alt: fallback.alt || ''}]
                : [];

        if (sources.length < 1) return;

        const track = document.createElement('div');
        track.className = 'track';
        // SLICD-34: pojedyncze zdjęcie - przenosimy istniejący <img> (z srcset, lazy i data-large-image)
        // zamiast tworzyć nowy z samego src, który pobierałby się od razu w pełnym rozmiarze.
        const reuseFallback = !thumbItems.length && fallback;
        sources.forEach(({src, alt}) => {
            const slide = document.createElement('div');
            slide.className = 'slide';
            if (reuseFallback) {
                slide.appendChild(fallback);
            } else {
                slide.innerHTML = `<img src="${src}" alt="${alt}">`;
            }
            track.appendChild(slide);
        });
        holder.replaceChildren(track);

        let index = 0;
        let slideWidth = 0;
        let startX = 0;
        let deltaX = 0;
        let dragging = false;

        const update = (animate = true) => {
            slideWidth = holder.clientWidth;
            Array.from(track.children).forEach(slide => {
                slide.style.width = `${slideWidth}px`;
            });
            track.style.transition = animate ? 'transform .3s ease' : 'none';
            track.style.transform = `translateX(${-index * slideWidth}px)`;

            thumbItems.forEach((item, itemIndex) => {
                item.classList.toggle('active', itemIndex === index);
            });
        };

        const goTo = nextIndex => {
            index = (nextIndex + sources.length) % sources.length;
            update();
        };

        prevButton?.addEventListener('click', event => {
            event.preventDefault();
            goTo(index - 1);
        });
        nextButton?.addEventListener('click', event => {
            event.preventDefault();
            goTo(index + 1);
        });

        thumbItems.forEach((item, itemIndex) => {
            item.addEventListener('click', () => goTo(itemIndex));
        });

        holder.addEventListener('pointerdown', event => {
            if (event.button !== 0 || sources.length < 2) return;
            dragging = true;
            startX = event.clientX;
            deltaX = 0;
            track.style.transition = 'none';
            holder.setPointerCapture?.(event.pointerId);
        }, {passive: true});

        holder.addEventListener('pointermove', event => {
            if (!dragging) return;
            deltaX = event.clientX - startX;
            track.style.transform = `translateX(${-(index * slideWidth) + deltaX}px)`;
            if (Math.abs(deltaX) > 5) event.preventDefault();
        }, {passive: false});

        const endDrag = event => {
            if (!dragging) return;
            dragging = false;
            holder.releasePointerCapture?.(event.pointerId);
            if (Math.abs(deltaX) > 40) goTo(deltaX < 0 ? index + 1 : index - 1);
            else update();
        };

        holder.addEventListener('pointerup', endDrag);
        holder.addEventListener('pointercancel', endDrag);
        window.addEventListener('resize', () => update(false));
        update(false);
    });
});

/*
 * ===== Gallery carousel =====
(() => {
    const carousels = document.querySelectorAll('[data-carousel]');
    if (!carousels.length) return;

    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

    const getPerView = () => {
        if (window.matchMedia('(max-width: 560px)').matches) return 1;
        if (window.matchMedia('(max-width: 768px)').matches) return 2;
        if (window.matchMedia('(max-width: 1440px)').matches) return 3;
        return 4;
    };

    carousels.forEach(root => {
        const track = root.querySelector('[data-carousel-track]');
        if (!track) return;

        const slides = Array.from(track.children);
        if (!slides.length) return;

        let dotsWrap = root.querySelector('[data-carousel-dots]');
        if (!dotsWrap) {
            dotsWrap = document.createElement('div');
            dotsWrap.className = 'gallery__images__dots';
            dotsWrap.setAttribute('data-carousel-dots', '');
            dotsWrap.setAttribute('aria-label', 'Nawigacja karuzeli');
            root.appendChild(dotsWrap);
        }

        let index = 0;

        const getMetrics = () => {
            const slideWidth = slides[0].getBoundingClientRect().width;
            const styles = getComputedStyle(track);
            const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
            const perView = getPerView();
            const maxIndex = Math.max(0, slides.length - perView);

            return {
                step: slideWidth + gap,
                maxIndex,
            };
        };

        const setActiveDot = () => {
            dotsWrap.querySelectorAll('.gallery__images__dot').forEach((dot, dotIndex) => {
                dot.classList.toggle('gallery__images__dot--active', dotIndex === index);
            });
        };

        const goTo = nextIndex => {
            const { step, maxIndex } = getMetrics();
            index = clamp(nextIndex, 0, maxIndex);
            track.style.transform = `translate3d(${-index * step}px, 0, 0)`;
            setActiveDot();
        };

        const renderDots = () => {
            const count = Math.max(1, slides.length - getPerView() + 1);
            dotsWrap.innerHTML = '';

            for (let dotIndex = 0; dotIndex < count; dotIndex++) {
                const dot = document.createElement('button');
                dot.type = 'button';
                dot.className = 'gallery__images__dot';
                dot.setAttribute('aria-label', `Przejdź do slajdu ${dotIndex + 1}`);
                dot.addEventListener('click', () => goTo(dotIndex));
                dotsWrap.appendChild(dot);
            }

            setActiveDot();
        };

        const init = () => {
            const { maxIndex } = getMetrics();
            index = clamp(index, 0, maxIndex);
            goTo(index);
            renderDots();
        };

        window.addEventListener('resize', init);
        init();
    });
})();
*/
